<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DraggableReorderList from 'dashboard/components-next/DraggableReorderList/DraggableReorderList.vue';
import ListFormDialog from './components/ListFormDialog.vue';

const props = defineProps({
  boardId: { type: String, required: true },
});

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const boardIdNum = computed(() => Number(props.boardId));
const board = useMapGetter('kanban/boards/getBoardById');
const activeBoard = computed(() => board.value(boardIdNum.value));
const getListsByBoard = useMapGetter('kanban/lists/getListsByBoard');
const lists = computed(() => getListsByBoard.value(boardIdNum.value));
const uiFlags = computed(() => getters['kanban/lists/getUIFlags'].value);

// I7: an unhandled rejection here left the screen showing "no lists yet" after a failed load.
const notifyLoadFailure = () => useAlert(t('KANBAN.SETTINGS.LOAD_ERROR'));
store.dispatch('kanban/boards/get').catch(notifyLoadFailure);
store.dispatch('kanban/lists/get', boardIdNum.value).catch(notifyLoadFailure);

const dialogRef = ref(null);
const editingList = ref(null);

const openCreate = () => {
  editingList.value = null;
  dialogRef.value.dialogRef.open();
};

const openEdit = list => {
  editingList.value = list;
  dialogRef.value.dialogRef.open();
};

const handleReorder = async positionsHash => {
  try {
    await store.dispatch('kanban/lists/reorder', {
      kanbanBoardId: boardIdNum.value,
      positionsHash,
    });
  } catch (error) {
    useAlert(t('KANBAN.SETTINGS.LIST_API.REORDER_ERROR'));
  }
};

const showDeletePopup = ref(false);
const selectedList = ref({});
const loading = ref({});

const openDelete = list => {
  selectedList.value = list;
  showDeletePopup.value = true;
};

const confirmDeletion = async () => {
  const { id } = selectedList.value;
  loading.value[id] = true;
  showDeletePopup.value = false;
  try {
    await store.dispatch('kanban/lists/delete', id);
    useAlert(t('KANBAN.SETTINGS.LIST_API.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('KANBAN.SETTINGS.LIST_API.DELETE_ERROR'));
  } finally {
    loading.value[id] = false;
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('KANBAN.SETTINGS.LOADING')"
    :no-records-found="!lists.length"
    :no-records-message="t('KANBAN.SETTINGS.NO_LISTS')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="activeBoard?.name || ''"
        :description="t('KANBAN.SETTINGS.DESCRIPTION')"
        feature-name="kanban"
      >
        <template #actions>
          <router-link
            :to="{ name: 'kanban_settings_list' }"
            class="text-body-main text-n-slate-11"
          >
            {{ t('KANBAN.SETTINGS.BACK_TO_BOARDS') }}
          </router-link>
          <Button
            v-if="isAdmin"
            :label="t('KANBAN.SETTINGS.NEW_LIST')"
            size="sm"
            @click="openCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <DraggableReorderList
        :items="lists"
        item-key="id"
        :disabled="!isAdmin"
        @reorder="handleReorder"
      >
        <template #item="{ item }">
          <div
            class="flex justify-between items-center gap-4 py-3 border-b border-n-weak"
          >
            <div class="flex flex-col">
              <span class="text-body-main text-n-slate-12">{{
                item.title
              }}</span>
              <span
                v-if="item.webhook_url"
                class="text-label-small text-n-slate-11 truncate max-w-md"
              >
                {{ item.webhook_url }}
              </span>
            </div>
            <div class="flex gap-2">
              <Button
                v-if="isAdmin"
                icon="i-woot-settings"
                slate
                sm
                @click="openEdit(item)"
              />
              <Button
                v-if="isAdmin"
                icon="i-woot-bin"
                slate
                sm
                :is-loading="loading[item.id]"
                @click="openDelete(item)"
              />
            </div>
          </div>
        </template>
        <template #ghost="{ item }">
          <div
            class="flex justify-between items-center gap-4 py-3 px-4 bg-n-solid-2 border border-n-weak rounded-lg"
          >
            <span class="text-body-main text-n-slate-12">{{ item.title }}</span>
          </div>
        </template>
      </DraggableReorderList>
    </template>
    <ListFormDialog
      ref="dialogRef"
      :list="editingList"
      :board-id="boardIdNum"
    />
    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="
        t('KANBAN.SETTINGS.LIST_DELETE.CONFIRM_TITLE', {
          listTitle: selectedList.title,
        })
      "
      :message="t('KANBAN.SETTINGS.LIST_DELETE.CONFIRM_MESSAGE')"
      :confirm-text="t('KANBAN.SETTINGS.LIST_DELETE.CONFIRM_YES')"
      :reject-text="t('KANBAN.SETTINGS.LIST_DELETE.CONFIRM_NO')"
      :confirm-value="selectedList.title"
      :confirm-place-holder-text="
        t('KANBAN.SETTINGS.LIST_DELETE.CONFIRM_PLACEHOLDER')
      "
      @on-confirm="confirmDeletion"
      @on-close="showDeletePopup = false"
    />
  </SettingsLayout>
</template>
